import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Avatar, IconButton, CircularProgress, Tooltip, Badge } from '@mui/material';
import { PhotoCamera, Delete } from '@mui/icons-material';
import api from '../config/api';

const ProfilePicture = ({ currentUrl, onUpdate, userId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('profilePicture.imageSizeError'));
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError(t('profilePicture.fileTypeError'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create a FormData object for multipart/form-data request
      const formData = new FormData();
      formData.append('profilePic', file);

      // Add userId (which is actually email) to the form data
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Use the provided userId (email) or fall back to user.email from localStorage
      formData.append('userId', userId || user.email);

      // Make the request to upload the profile picture
      const response = await api.post('/auth/upload-profile-pic', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Update the profile picture URL in the parent component
        onUpdate(response.data.filePath);

        // Update localStorage
        if (user && user.email) {
          user.profile_picture_url = response.data.filePath;
          localStorage.setItem('user', JSON.stringify(user));

          // Dispatch an event to notify other components (like Header) that user data changed
          window.dispatchEvent(new Event('userDataChanged'));
        }
      } else {
        setError(t('profilePicture.uploadFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('profilePicture.uploadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!currentUrl) return;

    setLoading(true);
    setError('');

    try {
      // Get user ID (email) from props or localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userIdToUse = userId || user.email;

      const response = await api.post('/auth/remove-profile-picture', { userId: userIdToUse });

      if (response.data.success) {
        // Update the profile picture URL in the parent component
        onUpdate(null);

        // Update localStorage
        if (user && user.email) {
          user.profile_picture_url = null;
          localStorage.setItem('user', JSON.stringify(user));

          // Dispatch an event to notify other components (like Header) that user data changed
          window.dispatchEvent(new Event('userDataChanged'));
        }
      } else {
        setError(t('profilePicture.removeFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('profilePicture.removeFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          <Tooltip title={t('profilePicture.uploadPhoto')}>
            <label htmlFor="profile-picture-upload">
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-picture-upload"
                type="file"
                onChange={handleImageUpload}
              />
              <IconButton
                color="primary"
                component="span"
                sx={{
                  backgroundColor: 'background.paper',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': { backgroundColor: 'background.paper' }
                }}
                size="small"
              >
                {loading ? <CircularProgress size={20} /> : <PhotoCamera fontSize="small" />}
              </IconButton>
            </label>
          </Tooltip>
        }
      >
        <Avatar
          src={currentUrl}
          sx={{
            width: 120,
            height: 120,
            border: '4px solid',
            borderColor: 'background.paper',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.08)'
          }}
        />
      </Badge>

      {currentUrl && (
        <Tooltip title={t('profilePicture.removePhoto')}>
          <IconButton
            color="error"
            onClick={handleRemoveProfilePicture}
            disabled={loading}
            size="small"
            sx={{
              mt: 1,
              backgroundColor: 'background.paper',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {error && (
        <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.75rem', textAlign: 'center', maxWidth: 150 }}>
          {error}
        </Box>
      )}
    </Box>
  );
};

export default ProfilePicture;